import {computed, inject, Injectable, signal, WritableSignal} from '@angular/core';
import {AuthInfo} from "../../models/auth-info";
import {LoggerService} from "../../services/logger/logger-service/logger.service";
import {MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService} from '@azure/msal-angular';
import {AuthenticationResult, EventMessage, EventType, InteractionStatus, RedirectRequest} from '@azure/msal-browser';
import {filter, Subject, takeUntil} from "rxjs";
import {SystemParamsService} from "../../services/system-params-service/system-params.service";
import {environment} from "../../../../environments/environment";

/**
 * Uses MSAL to authenticate users and handle roles.
 */
@Injectable({
  providedIn: 'root'
})
export class AppMsalAuthService{
  private readonly _destroying$ = new Subject<void>();

  // Injectables
  private logger = inject(LoggerService);
  private msalService = inject(MsalService);
  private msalBroadcastService = inject(MsalBroadcastService);
  private msalGuardConfig = inject<MsalGuardConfiguration>(MSAL_GUARD_CONFIG);
  private systemsParamsService = inject(SystemParamsService);

  private token: string = '';

  // Hide writable signal from consumers
  private _user: WritableSignal<AuthInfo | null> = signal(null);
  private _loaded: WritableSignal<boolean> = signal(false);
  private _tenantMatches: WritableSignal<boolean> = signal(false);

  // Expose non-writable signal
  user = this._user.asReadonly();
  name = computed(() => this._user()?.name ?? null)
  loaded = this._loaded.asReadonly();
  tenantMatches = this._tenantMatches.asReadonly();

  login() {
    this.msalService.loginRedirect({...this.msalGuardConfig.authRequest} as RedirectRequest);
  }

  logout() {
    this._loaded.set(false);
    this.msalService.logoutRedirect();
    this._user.set(null);

    this.logger.debug(`User logged out. User signal now ${this.user()}}`)
  }

  isLoggedIn(): boolean {
    return this.msalService.instance.getAllAccounts().length > 0; // this.user() !== null;
  }

  checkTenantMatches(): void {
    const account = this.msalService.instance.getActiveAccount();
    this._tenantMatches.set(account?.tenantId === environment.tenantId);
  }

  getToken(): string {
    return this.token;
  }

  /**
   * Sets user from claims of active account.
   * @private
   */
  private setUser() {
    const account = this.msalService.instance.getActiveAccount();

    if (account === null) {
      return;
    }

    const claims = this.getClaims(account.idTokenClaims as Record<string, any>);
    const name = claims.filter((x: { claim: string; }) => x.claim === "name")[0]?.value ?? '';

    this._user.set({name: name});
    this.token = account.idToken ?? '';
  }

  /**
   * Transforms claims object to an array of claims.
   * @param claims Claims record to transform.
   * @private
   */
  private getClaims(claims: Record<string, any>) {
    console.log(claims)
    let claimsList: any[] = [];

    if (claims) {
      Object.entries(claims).forEach((claim: [string, unknown], index: number) => {
        claimsList.push({id: index, claim: claim[0], value: claim[1]});
      });
    }

    return claimsList;
  }

  setupSubscriptions(): void {
    this.msalService.handleRedirectObservable().subscribe(); // Subscribing to handleRedirectObservable before any other functions both initializes the application and ensures redirects are handled

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount();
        this.setUser();
        this.checkTenantMatches();

        // Check if there's a token and set _loaded signal
        const account = this.msalService.instance.getActiveAccount();
        if (account) {
          this._loaded.set(true);
        }

      })

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS
          || msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
          || msg.eventType === EventType.SSO_SILENT_SUCCESS),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        let payload = result.payload as AuthenticationResult;
        this.msalService.instance.setActiveAccount(payload.account);
        this.setUser();
        this._loaded.set(true);
        this.systemsParamsService.loadSystemParams();
        return result;
      });
  }

  private checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    let activeAccount = this.msalService.instance.getActiveAccount();

    if (
      !activeAccount &&
      this.msalService.instance.getAllAccounts().length > 0
    ) {
      let accounts = this.msalService.instance.getAllAccounts();
      this.msalService.instance.setActiveAccount(accounts[0]);
    }
  }
}
